import React, { useState, useEffect, useCallback } from "react";
import { CapitalLimits } from "./CapitalLimits";
import { Zap, Loader2, ShieldAlert, ShieldCheck, Bitcoin, LineChart, RefreshCw, KeyRound, Copy, CheckCircle2, XCircle, AlertTriangle, TrendingUp } from "lucide-react";
import { brokerSettings, brokerSaveSettings, brokerSaveAlpaca, brokerGenRhKeys, brokerSaveRobinhood, brokerSaveCoinbase, brokerSaveKalshi, brokerAccount, brokerOrders, brokerPositions, brokerPnl } from "../lib/api";
import { toast } from "sonner";
import { NexusMoney } from "./NexusMoney";
import { NexusShortsReport } from "./NexusShortsReport";
import { NexusOracle } from "./NexusOracle";
import { brokerLiveStatus } from "../lib/api";

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-cyan-300/50 text-[11px] uppercase tracking-wider">{label}</label>
    {children}
  </div>
);
const inputCls = "w-full bg-[#0a1020] border border-[#ffffff18] rounded-lg px-3 py-2 text-sm text-cyan-50 placeholder-cyan-200/20 focus:border-[#FFD700] outline-none";

const ConnectedBanner = ({ label, extra, onReconnect }) => (
  <div className="flex items-center justify-between rounded-lg bg-emerald-500/10 border border-emerald-500/25 px-3 py-2">
    <span className="text-emerald-200 text-xs font-semibold">✓ Connected & auto-saved — {label}{extra ? ` · ${extra}` : ""}</span>
    <button onClick={onReconnect} className="text-cyan-300/50 hover:text-cyan-100 text-[11px] underline">Re-connect</button>
  </div>
);

export default function NexusExecution({ session }) {
  const userId = session.user_id;
  const [cfg, setCfg] = useState(null);
  const [engineOn, setEngineOn] = useState(true);
  useEffect(() => { brokerLiveStatus(session.user_id).then((d) => setEngineOn(!!d.engine_on)).catch(() => {}); }, [session.user_id]);
  const [acct, setAcct] = useState(null);
  const [orders, setOrders] = useState([]);
  const [positions, setPositions] = useState([]);
  const [posStats, setPosStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  // form state
  const [alKey, setAlKey] = useState("");
  const [alSecret, setAlSecret] = useState("");
  const [alPaper, setAlPaper] = useState(false);
  const [rhApi, setRhApi] = useState("");
  const [pubKey, setPubKey] = useState("");
  const [cbName, setCbName] = useState("");
  const [cbPem, setCbPem] = useState("");
  const [kId, setKId] = useState("");
  const [kPem, setKPem] = useState("");
  const [reconnect, setReconnect] = useState({});
  const [perCap, setPerCap] = useState(150);
  const [dailyCap, setDailyCap] = useState(1000);
  const [minConf, setMinConf] = useState(70);
  const [pnl, setPnl] = useState(null);

  const load = useCallback(async () => {
    try {
      const c = await brokerSettings(userId);
      setCfg(c);
      setPerCap(c.per_trade_cap); setDailyCap(c.daily_cap); setMinConf(c.min_confidence ?? 70);
      setPubKey(c.robinhood?.public_key || "");
      const [a, o] = await Promise.all([brokerAccount(userId), brokerOrders(userId)]);
      setAcct(a); setOrders(o.items || []);
      try { const pp = await brokerPositions(userId); setPositions(pp.items || []); setPosStats(pp.stats || {}); } catch (e) {}
      try { setPnl(await brokerPnl(userId)); } catch (e) {}
    } catch (e) {} finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(okMsg); try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(""); }
  };

  const toggleArm = () => {
    const next = !cfg.armed;
    if (next && !window.confirm("ARM auto-execution? Trades that clear all 5 agents will be placed with REAL money automatically, up to your caps.")) return;
    act(() => brokerSaveSettings(userId, { armed: next }), next ? "Auto-execution ARMED" : "Auto-execution disarmed");
  };
  const saveCaps = () => act(() => brokerSaveSettings(userId, { per_trade_cap: Number(perCap), daily_cap: Number(dailyCap) }), "Caps saved");
  const saveAlpaca = () => act(() => brokerSaveAlpaca(userId, { key: alKey.trim(), secret: alSecret.trim(), paper: alPaper }).then(() => { setAlKey(""); setAlSecret(""); }), "Alpaca connected");
  const genKeys = () => act(async () => { const r = await brokerGenRhKeys(userId); setPubKey(r.public_key); }, "Key pair generated");
  const saveRh = () => act(() => brokerSaveRobinhood(userId, { api_key: rhApi.trim() }).then(() => setRhApi("")), "Robinhood connected");
  const saveCb = () => act(() => brokerSaveCoinbase(userId, { key_name: cbName.trim(), private_key: cbPem.trim() }).then(() => { setCbName(""); setCbPem(""); }), "Coinbase connected");
  const saveKalshi = () => act(() => brokerSaveKalshi(userId, { key_id: kId.trim(), private_key: kPem.trim(), env: "prod" }).then(() => { setKId(""); setKPem(""); }), "Kalshi connected");

  if (loading) return <div className="h-full grid place-items-center bg-[#030712]"><Loader2 className="w-6 h-6 animate-spin text-[#FFD700]" /></div>;

  const armed = cfg?.armed;
  const al = cfg?.alpaca || {}, rh = cfg?.robinhood || {}, cb = cfg?.coinbase || {}, kal = cfg?.kalshi || {};
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="execution-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-[#FFD700] font-bold text-2xl flex items-center gap-2"><Zap className="w-6 h-6" /> Live Execution</h2>
            <p className="text-cyan-200/60 text-sm mt-1">Trades that clear all 5 agents are placed automatically on <b>Coinbase</b> — stocks and crypto from one account. Connect Robinhood too and crypto auto-routes to whichever venue is cheaper. Long/buy only, bound by your caps.</p>
          </div>
          <button onClick={load} data-testid="exec-refresh" className="text-cyan-300/50 hover:text-cyan-100 p-2"><RefreshCw className="w-4 h-4" /></button>
        </div>

        {/* ARM master switch */}
        <div className={`rounded-xl border p-4 flex items-center justify-between gap-3 ${armed ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#ffffff14] bg-[#0a1020]"}`}>
          <div className="flex items-center gap-3">
            {armed ? <ShieldCheck className="w-7 h-7 text-emerald-300" /> : <ShieldAlert className="w-7 h-7 text-cyan-300/50" />}
            <div>
              <p className="font-bold text-cyan-50">{armed && engineOn ? "Auto-execution is LIVE" : armed ? "Armed, but this staging copy never trades (only the live app does)" : "Auto-execution is OFF"}</p>
              <p className="text-xs text-cyan-200/50">{armed ? "Winners are being placed with real money." : "The kill-switch is engaged. Nothing is placed."}</p>
            </div>
          </div>
          <button onClick={toggleArm} data-testid="exec-arm-toggle"
            className={`px-4 py-2 rounded-lg text-xs font-bold ${armed ? "bg-rose-500/90 text-white hover:bg-rose-500" : "bg-[#FFD700] text-black hover:brightness-110"}`}>
            {busy && busy.includes("ARM") ? <Loader2 className="w-4 h-4 animate-spin" /> : armed ? "KILL-SWITCH" : "ARM"}
          </button>
        </div>

        {/* Caps */}
        <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4 space-y-3">
          <p className="text-cyan-100 font-semibold text-sm">Risk Caps</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Per-trade ($)"><input type="number" value={perCap} onChange={(e) => setPerCap(e.target.value)} className={inputCls} data-testid="exec-per-cap" /></Field>
            <Field label="Daily ($)"><input type="number" value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} className={inputCls} data-testid="exec-daily-cap" /></Field>
          </div>
          <Field label={`High-probability gate — only trade ideas ≥ ${minConf}% confidence`}>
            <div className="flex items-center gap-3" data-testid="exec-gate-readonly">
              <div className="flex-1 h-1.5 rounded-full bg-[#ffffff14] overflow-hidden">
                <div className="h-full bg-[#FFD700]" style={{ width: `${minConf}%` }} />
              </div>
              <span className="text-[#FFD700] font-bold text-sm w-12 text-right">{minConf}%</span>
            </div>
            <p className="text-[11px] text-cyan-200/40 mt-1">Set this on <b className="text-cyan-200/70">The House</b> tab — it's the single gate for the whole engine.</p>
          </Field>
          <div className="flex items-center justify-between">
            <p className="text-xs text-cyan-200/50">Spent today: <span className="text-[#FFD700] font-semibold">${cfg?.spent_today ?? 0}</span> / ${cfg?.daily_cap}</p>
            <button onClick={saveCaps} data-testid="exec-save-caps" className="px-3 py-1.5 rounded-lg bg-[#ffffff12] hover:bg-[#ffffff20] text-cyan-100 text-xs font-bold">Save caps</button>
          </div>
        </div>

        <CapitalLimits />
        <NexusMoney userId={userId} />
        <NexusOracle userId={userId} />
        <NexusShortsReport userId={userId} />

        {/* P&L — realized by period + live unrealized */}
        {pnl && (
          <div className="rounded-2xl border border-[#ffffff14] bg-[#0a1020] p-4" data-testid="exec-pnl-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm"><LineChart className="w-4 h-4 text-[#FFD700]" /> Profit &amp; Loss</p>
              <p className="text-[11px] text-cyan-200/50">Open: <span className={`font-semibold ${pnl.open.unrealized >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="exec-pnl-unrealized">{pnl.open.unrealized >= 0 ? "+" : ""}${pnl.open.unrealized}</span> unrealized · {pnl.open.positions} pos · ${pnl.open.deployed} deployed</p>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {[["Today", "day"], ["Week", "week"], ["Month", "month"], ["Year", "year"], ["All-time", "all"]].map(([label, k]) => {
                const b = pnl[k] || {}; const up = (b.realized || 0) >= 0;
                return (
                  <div key={k} className="rounded-xl border border-[#ffffff10] bg-[#0b1322] p-3 text-center" data-testid={`exec-pnl-${k}`}>
                    <p className="text-[10px] uppercase tracking-wide text-cyan-200/40 mb-1">{label}</p>
                    <p className={`text-base font-bold ${up ? "text-emerald-300" : "text-rose-300"}`}>{up ? "+" : ""}${b.realized ?? 0}</p>
                    <p className={`text-[10px] ${up ? "text-emerald-300/60" : "text-rose-300/60"}`}>{up ? "+" : ""}{b.return_pct ?? 0}%</p>
                    <p className="text-[10px] text-cyan-200/40 mt-1">{b.trades ?? 0} trades · {b.win_rate ?? 0}% win</p>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-cyan-200/30 mt-2">Realized = closed trades in each period (to-date, UTC). Unrealized = live mark on open positions.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4">
            <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm mb-2"><LineChart className="w-4 h-4 text-emerald-300" /> Alpaca (stocks)</p>
            {acct?.alpaca ? (acct.alpaca.error ? <p className="text-rose-300 text-xs">{acct.alpaca.error}</p> :
              <div className="text-xs text-cyan-200/70 space-y-0.5">
                <p>Buying power: <span className="text-[#FFD700] font-semibold">${acct.alpaca.buying_power}</span></p>
                <p>Equity: ${acct.alpaca.equity} {cfg.alpaca.paper && <span className="text-cyan-300/40">(paper)</span>}</p>
              </div>) : <p className="text-cyan-200/40 text-xs">Not connected</p>}
          </div>
          <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4">
            <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm mb-2"><Bitcoin className="w-4 h-4 text-orange-300" /> Robinhood (crypto)</p>
            {acct?.robinhood ? (acct.robinhood.error ? <p className="text-rose-300 text-xs">{acct.robinhood.error}</p> :
              <p className="text-xs text-emerald-300">Connected ✓</p>) : <p className="text-cyan-200/40 text-xs">Not connected</p>}
          </div>
          <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4">
            <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm mb-2"><Bitcoin className="w-4 h-4 text-blue-300" /> Coinbase (crypto)</p>
            {acct?.coinbase ? (acct.coinbase.error ? <p className="text-rose-300 text-xs">{acct.coinbase.error}</p> :
              <div className="text-xs text-cyan-200/70 space-y-0.5" data-testid="exec-cb-balances">
                <p>Spot USD (longs): <span className="text-[#FFD700] font-semibold">${acct.coinbase.usd_available}</span></p>
                <p>Futures buying power (shorts): <span className={`font-semibold ${Number(acct.coinbase.futures_buying_power) > 0 ? "text-[#FFD700]" : "text-rose-300"}`}>${acct.coinbase.futures_buying_power ?? 0}</span></p>
                {Number(acct.coinbase.futures_buying_power) <= 0 && (
                  <p className="text-[10px] text-rose-300/80 leading-snug" data-testid="exec-cb-futures-warn">SHORT ideas need the futures wallet funded. Spot cash and futures margin are separate buckets on Coinbase.</p>
                )}
              </div>) : <p className="text-cyan-200/40 text-xs">Not connected</p>}
          </div>
        </div>

        {/* Alpaca keys */}
        <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4 space-y-3">
          <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm">
            <LineChart className="w-4 h-4 text-emerald-300" /> Connect Alpaca (US stocks)
            {al.configured && <span className="text-[10px] text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {al.masked} {al.paper ? "· paper" : "· live"}</span>}
          </p>
          <Field label="API Key ID"><input value={alKey} onChange={(e) => setAlKey(e.target.value)} placeholder="PK..." className={inputCls} data-testid="exec-alpaca-key" /></Field>
          <Field label="Secret Key"><input value={alSecret} onChange={(e) => setAlSecret(e.target.value)} type="password" placeholder="••••••" className={inputCls} data-testid="exec-alpaca-secret" /></Field>
          <label className="flex items-center gap-2 text-xs text-cyan-200/70"><input type="checkbox" checked={alPaper} onChange={(e) => setAlPaper(e.target.checked)} data-testid="exec-alpaca-paper" /> These are PAPER keys (test first)</label>
          <button onClick={saveAlpaca} disabled={busy.includes("Alpaca")} data-testid="exec-save-alpaca" className="px-3 py-1.5 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5">
            {busy.includes("Alpaca") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Verify & connect
          </button>
        </div>

        {/* Robinhood keys */}
        <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4 space-y-3">
          <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm">
            <Bitcoin className="w-4 h-4 text-orange-300" /> Connect Robinhood (crypto)
            {rh.configured && <span className="text-[10px] text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {rh.masked}</span>}
          </p>
          <p className="text-[11px] text-cyan-200/50 leading-relaxed">
            Step 1 — generate a key pair here. Step 2 — in Robinhood (web → Crypto → API) add a key and paste the <b>public key</b> below. Step 3 — copy the <b>API key</b> Robinhood gives you and paste it here.
          </p>
          <div className="flex items-center gap-2">
            <button onClick={genKeys} data-testid="exec-gen-keys" className="px-3 py-1.5 rounded-lg bg-[#ffffff12] hover:bg-[#ffffff20] text-cyan-100 text-xs font-bold flex items-center gap-1.5">
              {busy.includes("pair") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Generate key pair
            </button>
            {rh.has_private && <span className="text-[10px] text-emerald-300/70">private key stored securely</span>}
          </div>
          {pubKey && (
            <Field label="Public key — paste this into Robinhood">
              <div className="flex gap-2">
                <input readOnly value={pubKey} className={inputCls + " font-mono text-[11px]"} data-testid="exec-pub-key" />
                <button onClick={() => copy(pubKey)} className="px-2 rounded-lg bg-[#ffffff12] hover:bg-[#ffffff20] text-cyan-100"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </Field>
          )}
          <Field label="Robinhood API Key (rh-api-…)"><input value={rhApi} onChange={(e) => setRhApi(e.target.value)} placeholder="rh-api-…" className={inputCls} data-testid="exec-rh-api" /></Field>
          <button onClick={saveRh} disabled={busy.includes("Robinhood")} data-testid="exec-save-rh" className="px-3 py-1.5 rounded-lg bg-orange-500/90 hover:bg-orange-500 text-white text-xs font-bold flex items-center gap-1.5">
            {busy.includes("Robinhood") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Verify & connect
          </button>
        </div>

        {/* Coinbase keys */}
        <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4 space-y-3">
          <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm">
            <Bitcoin className="w-4 h-4 text-blue-300" /> Connect Coinbase (crypto)
            {cb.configured && <span className="text-[10px] text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {cb.masked}</span>}
          </p>
          <p className="text-[11px] text-cyan-200/50 leading-relaxed">
            Create a key at <b>portal.cdp.coinbase.com/api-keys</b> → choose <b>ECDSA (ES256)</b>, enable <b>Trade</b> (not transfer/withdraw). Paste the key name and the EC private key below.
          </p>
          {cb.configured && !reconnect.cb ? (
            <ConnectedBanner label="Coinbase" extra={acct?.coinbase && !acct.coinbase.error ? `$${acct.coinbase.usd_available} USD` : cb.masked} onReconnect={() => setReconnect((r) => ({ ...r, cb: true }))} />
          ) : (<>
            <Field label="API Key Name (organizations/…/apiKeys/…)"><input value={cbName} onChange={(e) => setCbName(e.target.value)} placeholder="organizations/…/apiKeys/…" className={inputCls + " font-mono text-[11px]"} data-testid="exec-cb-name" /></Field>
            <Field label="EC Private Key (PEM)"><textarea value={cbPem} onChange={(e) => setCbPem(e.target.value)} rows={3} placeholder="-----BEGIN EC PRIVATE KEY-----&#10;…&#10;-----END EC PRIVATE KEY-----" className={inputCls + " font-mono text-[10px] resize-y"} data-testid="exec-cb-pem" /></Field>
            <button onClick={saveCb} disabled={busy.includes("Coinbase")} data-testid="exec-save-cb" className="px-3 py-1.5 rounded-lg bg-blue-500/90 hover:bg-blue-500 text-white text-xs font-bold flex items-center gap-1.5">
              {busy.includes("Coinbase") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Verify & connect
            </button>
          </>)}
        </div>

        {/* Kalshi keys (prediction markets) */}
        <div className="rounded-xl border border-[#ffffff14] bg-[#0a1020] p-4 space-y-3">
          <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm">
            <TrendingUp className="w-4 h-4 text-teal-300" /> Connect Kalshi (prediction markets)
            {kal.configured && <span className="text-[10px] text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {kal.masked} · {kal.env}</span>}
            {acct?.kalshi && !acct.kalshi.error && <span className="text-[10px] text-cyan-200/60">bal ${acct.kalshi.balance}</span>}
          </p>
          <p className="text-[11px] text-cyan-200/50 leading-relaxed">
            In the Kalshi app → <b>Settings / Account &amp; security → API Keys → Create</b>. Pick <b>Ed25519</b> (recommended). Paste the Access Key ID and the private key (begins <code>-----BEGIN PRIVATE KEY-----</code>).
          </p>
          {kal.configured && !reconnect.kal ? (
            <ConnectedBanner label="Kalshi" extra={acct?.kalshi && !acct.kalshi.error ? `$${acct.kalshi.balance} · ${kal.env}` : `${kal.masked} · ${kal.env}`} onReconnect={() => setReconnect((r) => ({ ...r, kal: true }))} />
          ) : (<>
            <Field label="Access Key ID (UUID)"><input value={kId} onChange={(e) => setKId(e.target.value)} placeholder="a1b2c3d4-…" className={inputCls + " font-mono text-[11px]"} data-testid="exec-kalshi-id" /></Field>
            <Field label="Private Key (PEM)"><textarea value={kPem} onChange={(e) => setKPem(e.target.value)} rows={3} placeholder="-----BEGIN PRIVATE KEY-----&#10;…&#10;-----END PRIVATE KEY-----" className={inputCls + " font-mono text-[10px] resize-y"} data-testid="exec-kalshi-pem" /></Field>
            <button onClick={saveKalshi} disabled={busy.includes("Kalshi")} data-testid="exec-save-kalshi" className="px-3 py-1.5 rounded-lg bg-teal-500/90 hover:bg-teal-500 text-white text-xs font-bold flex items-center gap-1.5">
              {busy.includes("Kalshi") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Verify & connect
            </button>
          </>)}
        </div>

        {/* Open positions (live, in & out) */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold">Live positions</p>
            {posStats.closed > 0 && <p className="text-[11px] text-cyan-200/50">Win rate <span className="text-[#FFD700] font-semibold">{posStats.win_rate}%</span> · {posStats.closed} closed</p>}
          </div>
          {positions.filter((p) => p.status === "open").length === 0 ? (
            <div className="text-center py-6 text-cyan-200/40 border border-[#ffffff0d] rounded-lg" data-testid="exec-positions-empty">
              <TrendingUp className="w-7 h-7 mx-auto mb-2 opacity-40" /><p className="text-sm">No open positions. The team opens them automatically when a trade clears all 5 agents.</p>
            </div>
          ) : positions.filter((p) => p.status === "open").map((p) => (
            <div key={p.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0a1020] border border-[#ffffff0d] text-sm" data-testid="exec-position-row">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${p.side === "short" ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>{(p.side || "long").toUpperCase()}</span>
              <span className="font-mono text-cyan-50 font-semibold">{p.symbol}</span>
              <span className="text-[10px] text-cyan-300/40 uppercase">{p.venue}</span>
              <span className={`ml-auto text-xs font-semibold ${(p.pnl_pct || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{p.pnl_pct != null ? `${p.pnl_pct}%` : "–"}</span>
            </div>
          ))}
        </div>

        {/* Order log (every entry & exit) */}
        <div className="space-y-1.5">
          <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold">Order ledger — in &amp; out ({orders.length})</p>
          {orders.length === 0 ? (
            <div className="text-center py-8 text-cyan-200/40 border border-[#ffffff0d] rounded-lg" data-testid="exec-orders-empty">
              <Zap className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No live orders yet. Arm execution, then run the House.</p>
            </div>
          ) : orders.map((o) => (
            <div key={o.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0a1020] border border-[#ffffff0d] text-sm" data-testid="exec-order-row">
              {o.status === "placed" ? <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" /> : o.status === "error" ? <XCircle className="w-4 h-4 text-rose-300 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />}
              <span className="font-mono text-cyan-50 font-semibold w-20 shrink-0">{o.symbol}</span>
              <span className="text-[10px] text-cyan-300/40 uppercase">{o.broker}</span>
              <span className="text-cyan-300/50 text-xs ml-auto">{o.status === "placed" ? `$${o.notional}` : o.error || o.status}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-cyan-200/30 text-center pb-4">Research & automation tool, not financial advice. You control the caps and the kill-switch.</p>
      </div>
    </div>
  );
}
